(function () {
  'use strict';

  // Cache fetched products across all bundle cards on the page so
  // shared components (typical for yarn) only fetch once.
  const productCache = {};

  function formatMoney(cents) {
    const format = window.shopMoneyFormat || '${{amount}}';
    const amount = (cents || 0) / 100;

    function withDelimiters(value, precision = 2, thousands = ',', decimal = '.') {
      const parts = value.toFixed(precision).split('.');
      const intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const decPart = parts[1] ? decimal + parts[1] : '';
      return intPart + decPart;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, placeholder) => {
      switch (placeholder) {
        case 'amount': return withDelimiters(amount, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(amount, 0, ',');
        case 'amount_with_comma_separator': return withDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(amount, 0, '.');
        case 'amount_with_apostrophe_separator': return withDelimiters(amount, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(amount, 0, ' ');
        case 'amount_with_space_separator': return withDelimiters(amount, 2, ' ', ',');
        case 'amount_with_period_and_space_separator': return withDelimiters(amount, 2, ' ', '.');
        default: return '';
      }
    });
  }

  async function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    try {
      const res = await fetch('/products/' + handle + '.js');
      if (!res.ok) return null;
      const data = await res.json();
      productCache[handle] = data;
      return data;
    } catch (e) {
      return null;
    }
  }

  // Pick the variant that should drive the "from" price for a component:
  // - explicit default if provided
  // - otherwise first available
  // - otherwise the first variant
  function selectVariant(product, defaultTitle) {
    if (!product || !product.variants || product.variants.length === 0) return null;
    if (defaultTitle) {
      const match = product.variants.find((v) => v.title === defaultTitle);
      if (match) return match;
    }
    return product.variants.find((v) => v.available) || product.variants[0];
  }

  async function calculateBundleTotal(components) {
    const products = await Promise.all(components.map((c) => fetchProduct(c.handle)));
    let total = 0;
    components.forEach((comp, i) => {
      const variant = selectVariant(products[i], comp.default_variant_title);
      if (!variant) return;
      total += variant.price * (comp.qty || 1);
    });
    return total;
  }

  async function processBundle(scriptEl) {
    let config;
    try {
      config = JSON.parse(scriptEl.textContent);
    } catch (e) {
      return;
    }
    if (!config || !Array.isArray(config.components) || config.components.length === 0) return;

    const total = await calculateBundleTotal(config.components);
    if (total <= 0) return;

    // Find the closest price element. The price span sits next to the script tag
    // inside the same product card.
    const card = scriptEl.closest('.product-item, [data-product-id]') || scriptEl.parentElement;
    if (!card) return;
    const priceEl = card.querySelector('[data-bundle-from-price]');
    if (!priceEl) return;

    const template = priceEl.dataset.bundleFromTpl || 'Fra __BUNDLE_PRICE__';
    priceEl.innerHTML = template.replace('__BUNDLE_PRICE__', formatMoney(total));
  }

  // Track which configs we've already processed so MutationObserver-driven
  // re-runs (e.g. predictive search injecting new HTML) don't re-fetch.
  const processed = new WeakSet();

  function processNewConfigs(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('script[data-bundle-config]').forEach((el) => {
      if (processed.has(el)) return;
      processed.add(el);
      processBundle(el);
    });
  }

  function init() {
    processNewConfigs(document);

    // Watch for AJAX-injected bundle cards (predictive search, infinite scroll, etc.)
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('script[data-bundle-config]')) {
            if (!processed.has(node)) {
              processed.add(node);
              processBundle(node);
            }
          } else if (node.querySelectorAll) {
            processNewConfigs(node);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
